import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import api from '@/utils/api';

/**
 * ReferralCapture — catches `?ref=CODE` on EVERY route and applies it.
 *
 * Until Oct 31 2026 only the homepage and /portrait parked the code, so an
 * invite link to /couple, /concierge or a blog post lost the friend's credit.
 * Now any page keeps it in localStorage (`ss_referral_code`), and the moment
 * the visitor has a session — a fresh registration, a Google sign-in, a
 * claimed journey — it is applied once. The backend refuses codes on accounts
 * older than a month and self-referrals, so the link cannot re-attribute an
 * existing member; those answers clear the stored code for good.
 */
export const REFERRAL_KEY = 'ss_referral_code';

let applying = null;

function applyStoredCode() {
  const code = localStorage.getItem(REFERRAL_KEY);
  if (!code || !localStorage.getItem('session_token')) return;
  if (applying === code) return;
  applying = code;
  api.post('/api/referral/apply', { code })
    .then(() => localStorage.removeItem(REFERRAL_KEY))
    .catch((err) => {
      // A clear "no" (used, invalid, own code, old account) is final; a
      // network blip keeps the code for the next page.
      const status = err?.response?.status;
      if (status === 400 || status === 404) localStorage.removeItem(REFERRAL_KEY);
      applying = null;
    });
}

export default function ReferralCapture() {
  const location = useLocation();

  useEffect(() => {
    try {
      const ref = new URLSearchParams(location.search).get('ref');
      if (ref && ref.trim()) localStorage.setItem(REFERRAL_KEY, ref.trim().toUpperCase());
      applyStoredCode();
    } catch {
      // Private mode or storage disabled — the register page's own apply still runs.
    }
  }, [location.pathname, location.search]);

  return null;
}
